import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import NontAuthorized401 from "NontAuthorized401";
import { apiCall } from "utils/apiClient";
import {
  FieldRow,
  HistoryItem,
  SectionBlock,
  SummaryBadge,
} from "./components/LeadDetailComponents";
import {
  extractFollowHistory,
  formatBudget,
  formatDateTime,
  formatLeadStatus,
  getLeadAssignee,
  getLeadAssignedBy,
} from "./leadDetailUtils";

function ChannelPartnerLeadDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const lead = location.state?.lead || null;
  const partnerId = location.state?.channelPartnerId;
  const partnerName = location.state?.channelPartnerName;
  const fallbackHistory = useMemo(() => extractFollowHistory(lead), [lead]);
  const [followHistory, setFollowHistory] = useState(fallbackHistory);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  useEffect(() => {
    setFollowHistory(fallbackHistory);
  }, [fallbackHistory]);

  useEffect(() => {
    let isActive = true;

    const fetchFollowHistory = async () => {
      if (!lead?.id) {
        if (isActive) {
          setHistoryError("");
          setHistoryLoading(false);
        }
        return;
      }

      setHistoryLoading(true);
      setHistoryError("");

      const result = await apiCall({
        endpoint: `admin/channel-partner/lead/${lead.id}/follow-ups`,
        method: "GET",
      });

      if (!isActive) return;

      if (result?.status === 200) {
        const apiHistory = extractFollowHistory(result?.data);
        setFollowHistory(apiHistory.length > 0 ? apiHistory : fallbackHistory);
        setHistoryLoading(false);
        return;
      }

      setFollowHistory(fallbackHistory);
      setHistoryError(result?.message || "Unable to load follow-up history.");
      setHistoryLoading(false);
    };

    fetchFollowHistory();

    return () => {
      isActive = false;
    };
  }, [fallbackHistory, lead?.id]);

  if (!localStorage.getItem("Token")) {
    return <NontAuthorized401 />;
  }

  if (!lead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div className="mt-6 px-4 sm:px-6">
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="text-lg font-semibold text-gray-900">Lead Detail</div>
            <p className="mt-2 text-sm text-gray-500">No lead data was provided for this page.</p>
            <button
              type="button"
              onClick={() =>
                navigate("/channel-partner-leads-list", {
                  state: partnerId
                    ? { channelPartnerId: partnerId, channelPartnerName: partnerName }
                    : undefined,
                })
              }
              className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Back to Leads
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const leadStatusLabel = formatLeadStatus(lead.status);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="relative mt-6 px-4 sm:px-6">
        <div className="pointer-events-none absolute inset-x-0 -top-6 h-24 bg-gradient-to-r from-slate-100 via-white to-slate-100 opacity-80" />
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white px-4 py-4 sm:px-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() =>
                    navigate("/channel-partner-leads-list", {
                      state: partnerId
                        ? { channelPartnerId: partnerId, channelPartnerName: partnerName }
                        : undefined,
                    })
                  }
                  className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 hover:underline"
                >
                  Back to Leads
                </button>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="truncate text-xl font-semibold text-slate-900 sm:text-2xl">
                    {lead.customer_name || "Lead Detail"}
                  </h1>
                  <span className="rounded-full bg-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white">
                    {leadStatusLabel}
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  {lead.mobile || "No mobile number available"}
                  {getLeadAssignee(lead) ? ` • ${getLeadAssignee(lead)}` : ""}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <SummaryBadge label="Budget" value={formatBudget(lead.budget)} tone="blue" />
                <SummaryBadge label="Space" value={lead.space_type || "-"} tone="emerald" />
                <SummaryBadge label="Assigned" value={getLeadAssignee(lead) || "Unassigned"} tone="amber" />
                <SummaryBadge
                  label="History"
                  value={historyLoading ? "Loading..." : `${followHistory.length} events`}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 px-4 py-4 lg:grid-cols-12 lg:px-6">
            <div className="space-y-4 lg:col-span-7">
              <SectionBlock title="Lead Information">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <FieldRow label="Customer" value={lead.customer_name || "-"} />
                  <FieldRow label="Mobile" value={lead.mobile || "-"} />
                  <FieldRow label="Address" value={lead.address || "-"} />
                  <FieldRow label="Requirement" value={lead.requirement_title || "-"} />
                  <FieldRow label="Space Type" value={lead.space_type || "-"} />
                  <FieldRow label="Budget" value={formatBudget(lead.budget)} />
                  <FieldRow label="Channel Partner" value={lead.channel_partner_name || "-"} />
                  <FieldRow label="Partner Company" value={lead.channel_partner_company_name || "-"} />
                  <FieldRow label="Partner Mobile" value={lead.channel_partner_mobile || "-"} />
                  <FieldRow label="Partner Status" value={formatLeadStatus(lead.channel_partner_status)} />
                  <FieldRow label="Notes" value={lead.notes || "-"} />
                </div>
              </SectionBlock>

              <SectionBlock title="Assignment" className="overflow-hidden">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <FieldRow label="Assigned Admin" value={getLeadAssignee(lead) || "Unassigned"} />
                  <FieldRow label="Assigned Admin Mobile" value={lead.assigned_admin_mobile || "-"} />
                  <FieldRow label="Assigned By" value={getLeadAssignedBy(lead) || "-"} />
                  <FieldRow label="Assigned At" value={formatDateTime(lead.assigned_at)} />
                  <FieldRow label="Last Follow Up" value={formatDateTime(lead.last_follow_up_at)} />
                  <FieldRow label="Created At" value={formatDateTime(lead.created_at)} />
                  <FieldRow label="Updated At" value={formatDateTime(lead.updated_at)} />
                </div>
              </SectionBlock>
            </div>

            <div className="lg:col-span-5">
              <SectionBlock title="Follow History" className="sticky top-6">
                <div className="max-h-[72vh] space-y-2 overflow-y-auto pr-1">
                  {historyError && (
                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                      {historyError}
                    </div>
                  )}
                  {followHistory.length > 0 ? (
                    followHistory.map((entry, index) => (
                      <HistoryItem
                        key={entry.id || `${entry.title}-${index}`}
                        title={entry.title}
                        assignedTo={entry.assignedTo}
                        timestamp={formatDateTime(entry.timestamp)}
                        description={entry.description}
                      />
                    ))
                  ) : historyLoading ? (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                      Loading follow history...
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                      No follow history available.
                    </div>
                  )}
                </div>
              </SectionBlock>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default ChannelPartnerLeadDetail;
